/**
 * Complete kinematic + engine state of the aircraft.
 *
 * World frame is NED (North, East, Down); `pos.d` is positive downward so
 * altitude = -pos.d. Orientation is stored as aviation Euler angles
 * (roll phi, pitch theta, yaw/heading psi) in radians, integrated from body
 * rates. Body axes: x forward (nose), y right (starboard wing), z down.
 */

export interface Vec3 {
  n: number;
  e: number;
  d: number;
}

export interface AircraftState {
  // Position in world NED (metres)
  pos: Vec3;
  // Velocity in world NED (m/s)
  vel: Vec3;
  // Orientation (radians)
  phi: number; // roll
  theta: number; // pitch
  psi: number; // heading (true), 0 = North, +east
  // Body angular rates (rad/s)
  p: number; // roll rate
  q: number; // pitch rate
  r: number; // yaw rate
  // Engine
  rpm: number;
  fuel: number; // litres remaining
  onGround: boolean;

  // Derived / bookkeeping (filled by the flight model each step)
  tas: number; // true airspeed m/s
  alpha: number; // angle of attack rad
  beta: number; // sideslip rad
  loadFactor: number; // g
  verticalSpeed: number; // m/s (positive up)
  altitude: number; // m MSL (=-pos.d)
}

export function createInitialState(): AircraftState {
  return {
    pos: { n: 0, e: 0, d: 0 },
    vel: { n: 0, e: 0, d: 0 },
    phi: 0,
    theta: 0,
    psi: 0,
    p: 0,
    q: 0,
    r: 0,
    rpm: 600,
    fuel: 150,
    onGround: true,
    tas: 0,
    alpha: 0,
    beta: 0,
    loadFactor: 1,
    verticalSpeed: 0,
    altitude: 0,
  };
}
